<template>
  <div class="max-w-[1000px] mx-auto border-[1px] border-gray-200 rounded-lg p-8 bg-white">
    <form @submit.prevent="onSubmit" class="space-y-6">
      <div class="space-y-4">
        <!-- Task Name -->
        <div class="space-y-2">
          <label
            class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            Task Title
          </label>
          <input
            v-model="form.taskName"
            placeholder="Enter task title"
            :disabled="loading || isReadOnly"
            class="bg-lightGray flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          />
        </div>

        <!-- Task URL -->
        <div class="space-y-2">
          <label
            class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            Task URL
          </label>
          <input
            v-model="form.taskUrl"
            placeholder="Enter task URL"
            type="url"
            :disabled="loading || isReadOnly"
            class="bg-lightGray flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          />
        </div>

        <!-- Section -->
        <div class="space-y-2">
          <label
            class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            Section
          </label>
          <select
            v-model="form.sectionId"
            :disabled="loading || sectionsStore.isLoading || isReadOnly"
            class="bg-lightGray flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <option value="">Select a section</option>
            <option v-for="section in sectionsStore.sections" :key="section.id" :value="section.id">
              {{ section.name }}
            </option>
          </select>
        </div>

        <!-- Assigned Mentor -->
        <div class="space-y-2">
          <label
            class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            Assigned Mentor
          </label>
          <select
            v-model="form.assignedMentor"
            :disabled="loading || usersStore.guidesAndMentorsLoading || isReadOnly"
            class="bg-lightGray flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <option value="">Select a mentor</option>
            <option v-for="mentor in usersStore.guidesAndMentors" :key="mentor.id" :value="mentor.id">
              {{ mentor.first_name }} {{ mentor.last_name }} ({{ mentor.username }})
            </option>
          </select>
        </div>

        <!-- Task Details -->
        <div class="space-y-2">
          <label
            class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            Task Details
          </label>
          <textarea
            v-model="form.taskDetails"
            placeholder="Enter task details"
            :disabled="loading || isReadOnly"
            class="bg-lightGray flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          ></textarea>
        </div>

        <!-- Numeric fields -->
        <div class="grid gap-4 grid-cols-1 sm:grid-cols-3">
          <!-- Points -->
          <div class="space-y-2">
            <label
              class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              Points
            </label>
            <input
              v-model="form.points"
              type="number"
              min="0"
              step="0.5"
              :disabled="loading || isReadOnly"
              class="bg-lightGray flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>

          <!-- Week Order -->
          <div class="space-y-2">
            <label
              class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              Week Order
            </label>
            <input
              v-model="form.weekOrder"
              type="number"
              :disabled="loading || isReadOnly"
              class="bg-lightGray flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>

          <!-- Order Within Week -->
          <div class="space-y-2">
            <label
              class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              Order Within Week
            </label>
            <input
              v-model="form.orderWithinWeek"
              type="number"
              min="1"
              :disabled="loading || isReadOnly"
              class="bg-lightGray flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>
        </div>

        <hr class="my-4" />
        <h2 class="text-sm text-gray-500 font-medium uppercase">Task Properties</h2>

        <div class="space-y-6">
          <!-- Is Required -->
          <div>
            <SwitchToggle
              v-model="form.isRequired"
              :disabled="loading || isReadOnly"
              label="Required Step"
              description="This task is a mandatory step in the process"
            />
          </div>

          <!-- Review Required -->
          <div>
            <SwitchToggle
              v-model="form.reviewRequired"
              :disabled="loading || isReadOnly"
              label="Review Required"
              description="This task requires review before completion"
            />
          </div>

          <!-- Fellow Reportable -->
          <div>
            <SwitchToggle
              v-model="form.fellowReportable"
              :disabled="loading || isReadOnly"
              label="Fellow Reportable"
              description="Fellows can report other fellows for this task"
            />
          </div>

          <!-- Has External Dependency -->
          <div>
            <SwitchToggle
              v-model="form.has_external_dependency"
              :disabled="loading || isReadOnly"
              label="Has External Dependency"
              description="This task depends on external factors like scheduling"
            />
          </div>
        </div>

        <hr class="my-4" />
      </div>

      <div v-if="!isReadOnly" class="flex justify-end space-x-2">
        <button
          type="button"
          @click="$emit('cancel')"
          :disabled="loading"
          class="inline-flex items-center justify-center rounded-md text-xs font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2"
        >
          Cancel
        </button>
        <button
          type="submit"
          :disabled="loading"
          class="inline-flex items-center justify-center rounded-md text-xs font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2"
        >
          <Loader2 v-if="loading" class="w-4 h-4 mr-2 animate-spin" />
          {{ loading ? 'Saving...' : 'Save Changes' }}
        </button>
      </div>

      <div
        v-if="statusMessage && !isReadOnly"
        class="mt-2 text-sm"
        :class="{ 'text-red-500': !isSuccessMessage, 'text-green-500': isSuccessMessage }"
      >
        {{ statusMessage }}
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, defineProps, defineEmits, onMounted, watch, computed } from 'vue'
import { Loader2 } from 'lucide-vue-next'
import { usePathStepsStore, type PathStep, type PathStepTask } from '@/stores/pathSteps'
import { useSectionsStore } from '@/stores/sections'
import { useUsersStore } from '@/stores/users'
import { SwitchToggle } from '@/components/ui/switch'
import { toast } from '@/components/ui/toast'

const props = defineProps<{
  pathStep: PathStep
  readOnly?: boolean
}>()

const emit = defineEmits<{
  (e: 'saved'): void
  (e: 'cancel'): void
}>()

// Reactive form object
const form = reactive({
  pathStepId: 0,
  taskId: 0,
  taskName: '',
  taskUrl: '',
  taskDetails: '',
  sectionId: '',
  assignedMentor: '',
  points: '',
  weekOrder: '',
  orderWithinWeek: '',
  pathId: 0,
  isRequired: true,
  reviewRequired: true,
  fellowReportable: false,
  has_external_dependency: false
})

const pathStepsStore = usePathStepsStore()
const sectionsStore = useSectionsStore()
const usersStore = useUsersStore()
const loading = ref(false)
const statusMessage = ref<string | null>(null)
const isSuccessMessage = ref(false)
const isReadOnly = computed(() => props.readOnly === true)

// Update the form with values from props
const updateForm = () => {
  if (!props.pathStep) {
    console.error('No pathStep provided to form')
    return
  }

  // Use Object.assign to update the reactive object
  Object.assign(form, {
    pathStepId: props.pathStep.id,
    taskId: props.pathStep.task.id,
    taskName: props.pathStep.task.name || '',
    taskUrl: props.pathStep.task.url || '',
    taskDetails: props.pathStep.task.details || '',
    sectionId: props.pathStep.task.section?.id?.toString() || '',
    assignedMentor: props.pathStep.task.assigned_mentor?.toString() || '',
    points: (props.pathStep.points ?? 0).toString(),
    weekOrder: (props.pathStep.week_order ?? 1).toString(),
    orderWithinWeek: (props.pathStep.order_within_week ?? 1).toString(),
    pathId: props.pathStep.path,
    isRequired: props.pathStep.is_required ?? true,
    reviewRequired: props.pathStep.task.review_required ?? true,
    fellowReportable: props.pathStep.task.fellow_reportable ?? false,
    has_external_dependency: props.pathStep.has_external_dependency ?? false
  })
}

// Watch for changes to the pathStep prop
watch(
  () => props.pathStep,
  () => {
    updateForm()
  },
  { immediate: true, deep: true }
)

// Also initialize on mount
onMounted(async () => {
  updateForm()
  // Fetch sections for the dropdown
  await sectionsStore.fetchSections()
  // Fetch guides and mentors for the dropdown
  await usersStore.fetchGuidesAndMentors()
})

// Handle form submission
const onSubmit = async () => {
  if (isReadOnly.value) return
  loading.value = true
  statusMessage.value = null

  try {
    // Format payload for API request
    const taskPayload: any = {
      id: form.taskId,
      name: form.taskName,
      url: form.taskUrl,
      details: form.taskDetails,
      review_required: form.reviewRequired,
      fellow_reportable: form.fellowReportable
    }

    // Only add section if it has a value
    if (form.sectionId) {
      taskPayload.section = parseInt(form.sectionId)
    }

    // Only add assigned_mentor if it has a value
    if (form.assignedMentor) {
      taskPayload.assigned_mentor = parseInt(form.assignedMentor)
    }

    const payload: Partial<PathStep> = {
      task: taskPayload as PathStepTask,
      week_order: parseInt(form.weekOrder) || 1,
      order_within_week: parseInt(form.orderWithinWeek) || 1,
      points: parseFloat(form.points) || 0,
      is_required: form.isRequired,
      path: form.pathId,
      has_external_dependency: form.has_external_dependency
    }

    console.log('Submitting path step update with payload:', payload)
    const success = await pathStepsStore.updatePathStep(form.pathStepId, payload)

    if (success) {
      statusMessage.value = 'Path step updated successfully'
      isSuccessMessage.value = true
      toast({
        title: 'Path step updated successfully',
        description: 'Changes have been saved to the curriculum.'
      })
      emit('saved')
    } else {
      statusMessage.value = pathStepsStore.error || 'Failed to update path step'
      isSuccessMessage.value = false
      toast({
        title: 'Failed to update path step',
        description: pathStepsStore.error || 'An error occurred while saving changes.',
        variant: 'destructive'
      })
    }
  } catch (error) {
    console.error('Error saving path step:', error)
    statusMessage.value = 'An unexpected error occurred'
    isSuccessMessage.value = false
    toast({
      title: 'An unexpected error occurred',
      description: 'Please try again or contact support if the problem persists.',
      variant: 'destructive'
    })
  } finally {
    loading.value = false
  }
}
</script>
